import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import Select from "components/ui/Select";
import SelectVendors from "components/ui/selectors/SelectVendors";
import SelectCustomers from "components/ui/selectors/SelectCustomers";
import SelectAreas from "components/ui/selectors/SelectAreas";
import Input from "components/ui/Input";
import { useAuthStore } from "store";
import { useGeneratePaymentReconReport } from "http-hooks/paymentreconmain/paymentreconmain";
import { useAllPartySupMasts } from "http-hooks/partysupmast/usepartysupmast";
import { getDocumentViewerRoute } from "utils/documentUrlHelper";

const reportParameterOptions = [
  { value: "D", label: "Default" },
  { value: "B", label: "B2B" }
];

const reportTypeOptions = [
  { value: "V", label: "Vendorwise" },
  { value: "C", label: "Customerwise" }
];

const reportOnOptions = [
  { value: "O", label: "Outstanding" },
  { value: "R", label: "Settled" },
  { value: "RO", label: "Settled & Outstanding" }
];

const filterOutOptions = [
  { value: "VCN", label: "Credit Note" },
  { value: "VDN", label: "Debit Note" }
];

export default function PaymentReconReportModal({ onClose }) {
  const navigate = useNavigate();
  const authData = useAuthStore((state) => state.authData);
  const agttp = authData?.agttp || 0;
  const userType = authData?.user_type || 0;

  const [reportParameter, setReportParameter] = useState({ value: "D", label: "Default" });
  const [reportType, setReportType] = useState(null);
  const [reportOn, setReportOn] = useState(null);
  const [selectedVendors, setSelectedVendors] = useState([]);
  const [selectedCustomers, setSelectedCustomers] = useState([]);
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [zeroHide, setZeroHide] = useState(false);
  const [vendorGroup, setVendorGroup] = useState(false);
  const [customerGroup, setCustomerGroup] = useState(false);
  const [selectedArea, setSelectedArea] = useState(null);
  const [filterOutTypes, setFilterOutTypes] = useState([]);

  const { mutateAsync: generateReport, isPending: isGenerating } = useGeneratePaymentReconReport();

  const showDateFields = reportOn?.value === "R" || reportOn?.value === "RO";
  const showZeroHiding = reportOn?.value === "R" || reportOn?.value === "RO";
  const showFilterOut = reportOn?.value === "O" || reportOn?.value === "RO";

  // Area filter shown whenever a customer selector is visible
  const showAreaFilter = !!reportType;

  // Fetch customers for the selected area
  const { data: { data: areaCustomers } = {} } = useAllPartySupMasts(
    {
      type: "C",
      level: 0,
      grplevel: 0,
      ag_status: 1,
      ...(selectedArea?.areaId ? { areaId: selectedArea.areaId } : {}),
      noPagination: true,
    },
    showAreaFilter && !!selectedArea
  );

  useEffect(() => {
    if (areaCustomers && areaCustomers.length > 0 && showAreaFilter) {
      setSelectedCustomers(areaCustomers);
    }
  }, [areaCustomers, showAreaFilter]);

  const handleAreaChange = (area) => {
    setSelectedArea(area);
    if (!area) {
      setSelectedCustomers([]);
    }
  };

  const filteredReportTypeOptions = reportTypeOptions.filter(option => {
    if (userType === 2) {
      return option.value === "C";
    }
    return true;
  });

  useEffect(() => {
    if (userType === 2) {
      setReportType({ value: "C", label: "Customerwise" });
      setReportOn({ value: "O", label: "Outstanding" });
    }
  }, [userType]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!reportType) {
      alert("Please select Report Type");
      return;
    }
    if (!reportOn) {
      alert("Please select Report On");
      return;
    }
    if (reportType.value === "V" && selectedVendors.length === 0) {
      alert("Please select at least one Vendor");
      return;
    }
    if (reportType.value === "C" && selectedCustomers.length === 0) {
      alert("Please select at least one Customer");
      return;
    }
    if (showDateFields && (!fromDate || !toDate)) {
      alert("Please select From Date and To Date");
      return;
    }

    const reportParams = {
      select_reptype: reportType.value,
      reporton: reportOn.value,
      fromdate: fromDate || null,
      todate: toDate || null,
      select_vendor: reportType.value === "V" ? selectedVendors[0]?.ps_Id : selectedVendors.map(v => v.ps_Id),
      select_cust: reportType.value === "C" ? selectedCustomers[0]?.ps_Id : selectedCustomers.map(c => c.ps_Id),
      zerohide: zeroHide ? 1 : 0,
      sltrecon: [],
      boolvendgroup: vendorGroup,
      boolcustgroup: customerGroup,
      filterOutTypes: filterOutTypes.map(t => t.value)
    };

    console.log("Generate Report with params:", reportParams);
    
      try {
        const result = await generateReport(reportParams);

        if (result.htmlUrl) {
          const baseUrl = process.env.REACT_APP_MAIN_BASE_URL || "";
          const htmlUrl = `${baseUrl}${result.htmlUrl}`;
          const typeLabel = reportType.value === "V" ? "Vendorwise" : "Customerwise";

          const route = getDocumentViewerRoute("payment-recon", {
            htmlUrl,
            pdfUrl: "",
            documentNumber: typeLabel,
            documentType: "Payment Reconciliation",
          });

          onClose();
          navigate(route);
        }
      } catch (error) {
        console.error("Error generating report:", error);
      }
  };

  return (
    <>
      {/* Header */}
      <div className="mb-4">
        <h2 className="text-lg font-semibold text-gray-900">
          Payment Reconciliation Report
        </h2>
      </div>

      {/* Form */}
      <form onSubmit={handleSubmit} className="space-y-4">
            {/* Report Parameter - Only for General (agttp=0) */}
            {agttp === 0 && (
              <Select
                label="Report Parameter"
                options={reportParameterOptions}
                value={reportParameter}
                onChange={setReportParameter}
                isRequired={true}
              />
            )}

            {/* Report Type */}
            <Select
              label="Report Type"
              options={filteredReportTypeOptions}
              value={reportType}
              onChange={(selected) => {
                setReportType(selected);
                setSelectedArea(null);
                setSelectedVendors([]);
                setSelectedCustomers([]);
              }}
              placeholder="Choose here"
              isRequired={true}
            />

            {/* Report On */}
            <Select
              label="Report On"
              options={reportOnOptions}
              value={reportOn}
              onChange={setReportOn}
              placeholder="Choose here"
              isRequired={true}
            />

            {/* Filter Out - Only for Outstanding reports */}
            {showFilterOut && (
              <Select
                label="Filter Out (Outstanding)"
                options={filterOutOptions}
                value={filterOutTypes}
                onChange={(val) => setFilterOutTypes(val || [])}
                placeholder="Select types to exclude"
                isMulti={true}
                isClearable={true}
              />
            )}

            {/* Vendor/Customer Groups - Only for General (agttp=0) */}
            {agttp === 0 && reportType && (
              <div className="space-y-3">
                <Input
                  type="checkbox"
                  label="Vendor Group"
                  value={vendorGroup}
                  onChange={(e) => setVendorGroup(e.target.checked)}
                />

                {(userType === 0 || (userType === 2 && authData?.allow_group_access)) && (
                  <Input
                    type="checkbox"
                    label="Customer Group"
                    value={customerGroup}
                    onChange={(e) => setCustomerGroup(e.target.checked)}
                  />
                )}
              </div>
            )}

            {/* Area Filter - shown when customer multi-select is active */}
            {showAreaFilter && (
              <SelectAreas
                value={selectedArea}
                onChange={handleAreaChange}
                label="Area (optional)"
                placeholder="Select area to auto-fill customers"
                showAddButton={false}
                isClearable={true}
              />
            )}

            {/* Vendorwise: show vendor first, then optional customer */}
            {reportType?.value === "V" && (
              <>
                {reportParameter.value === "B" && agttp === 0 ? (
                  <SelectCustomers
                    label="Vendor (B2B)"
                    value={selectedVendors}
                    onChange={setSelectedVendors}
                    placeholder="Select Customers"
                    isMulti={false}
                    isRequired={true}
                  />
                ) : (
                  <SelectVendors
                    label="Vendor"
                    value={selectedVendors}
                    onChange={setSelectedVendors}
                    placeholder="Select Vendors"
                    isMulti={agttp === 0}
                    isRequired={true}
                  />
                )}
                {agttp === 0 && (
                  <SelectCustomers
                    label="Customer (Optional)"
                    value={selectedCustomers}
                    onChange={setSelectedCustomers}
                    placeholder="Select Customers"
                    isMulti={true}
                    isClearable={true}
                  />
                )}
              </>
            )}

            {/* Customerwise: show customer first, then optional vendor */}
            {reportType?.value === "C" && (
              <>
                <SelectCustomers
                  label="Customer"
                  value={selectedCustomers}
                  onChange={setSelectedCustomers}
                  placeholder="Select Customers"
                  isMulti={agttp === 0}
                  isRequired={true}
                />
                {agttp === 0 && (
                  <SelectVendors
                    label="Vendor (Optional)"
                    value={selectedVendors}
                    onChange={setSelectedVendors}
                    placeholder="Select Vendors"
                    isMulti={true}
                    isClearable={true}
                  />
                )}
              </>
            )}

            {/* Date Range - Show for Settled reports */}
            {showDateFields && (
              <>
                <Input
                  label="From Date"
                  type="date"
                  value={fromDate}
                  onChange={(e) => setFromDate(e.target.value)}
                  withinFY={true}
                  isRequired={true}
                />
                <Input
                  label="To Date"
                  type="date"
                  value={toDate}
                  onChange={(e) => setToDate(e.target.value)}
                  withinFY={true}
                  isRequired={true}
                />
              </>
            )}

            {/* Zero Hiding - Show for Settled reports */}
            {showZeroHiding && (
              <Input
                type="checkbox"
                label="Zero Hiding"
                value={zeroHide}
                onChange={(e) => setZeroHide(e.target.checked)}
              />
            )}

        {/* Buttons */}
        <div className="flex justify-end gap-3 pt-3 border-t border-gray-200">
          <button
            type="button"
            onClick={onClose}
            className="rounded-md bg-white px-4 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={isGenerating}
            className="rounded-md bg-gray-600 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-gray-500 disabled:bg-gray-400 disabled:cursor-not-allowed"
          >
            {isGenerating ? "Generating..." : "Generate Report"}
          </button>
        </div>
      </form>
    </>
  );
}
